// frontend/user/src/_lib/use-file-upload.tsx
//
// 2026-05-28 重构(NC2CG 弹框对齐):原 UploadButton 用 `<Button>` 实色不符合
// NC2CG row 样式。拆成 hook 让 file-browser sheet 自渲染 row,
// 复用 store 与 UploadProgressPopup。

import { useRef, useState, useCallback } from 'react'
import { randomUUID } from '@dogdisk/shared'
import { runUpload } from './upload-run'
import { useUploadStore } from './upload-store'

export type UseFileUploadOptions = {
  parentId?: string
  onUploaded?: () => void
}

export type UseFileUploadResult = {
  // 隐藏的 <input type="file" multiple>,挂在 DOM 任意位置即可
  inputElement: React.ReactNode
  // trigger system file picker
  trigger: () => void
  // 当前是否处于"已开始上传" — 由 popup 显隐使用
  popupOpen: boolean
  // 关闭进度 popup(任务仍在 store 内继续)
  closePopup: () => void
}

export function useFileUpload({ parentId, onUploaded }: UseFileUploadOptions = {}): UseFileUploadResult {
  const inputRef = useRef<HTMLInputElement>(null)
  const [popupOpen, setPopupOpen] = useState(false)
  const addTask = useUploadStore((s) => s.addTask)

  const startOne = useCallback(
    (file: File) => {
      const id = randomUUID()
      addTask({
        id,
        name: file.name,
        size: file.size,
        bytesUploaded: 0,
        percent: 0,
        status: 'pending',
        startedAt: Date.now(),
        file,
      })
      runUpload(id, file, {
        ...(parentId ? { parentId } : {}),
        ...(onUploaded ? { onUploaded } : {}),
      })
    },
    [addTask, parentId, onUploaded],
  )

  const onPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      if (files.length === 0) return
      e.target.value = ''
      setPopupOpen(true)
      files.forEach((file) => startOne(file))
    },
    [startOne],
  )

  const trigger = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const closePopup = useCallback(() => setPopupOpen(false), [])

  const inputElement = (
    <input
      ref={inputRef}
      type="file"
      multiple
      className="hidden"
      onChange={onPick}
      data-testid="files-upload-input"
    />
  )

  return { inputElement, trigger, popupOpen, closePopup }
}
